import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { differenceInCalendarDays, formatDistance } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { BsBoxArrowInDown, BsBoxArrowUp } from 'react-icons/bs'
import { FaPencilAlt, FaSave, FaWhatsapp } from 'react-icons/fa'
import { FaBuildingUser, FaCheck, FaX } from 'react-icons/fa6'
import { ImCheckboxChecked, ImCheckboxUnchecked } from 'react-icons/im'
import { MdAddPhotoAlternate, MdCancel, MdOutlineImageNotSupported, MdTimer } from 'react-icons/md'
import { PiMicrosoftExcelLogoFill } from 'react-icons/pi'
import { RiShareBoxLine, RiUser2Fill } from 'react-icons/ri'
import { SiGooglemaps } from 'react-icons/si'
import { useParams } from 'react-router-dom'
import Select from 'react-select'
import { z } from 'zod'

import { Avatar } from '../../../components/Avatar'
import { Breadcrumb } from '../../../components/Breadcumb'
import { Button } from '../../../components/Button'
import { Dropdown } from '../../../components/Drowdown'
import { FloatingPromotersStayingTimeCounter } from '../../../components/FloatingPromotersStayingTimeCounter'
import { ModalAddImages } from '../../../components/ModalAddImages'
import { TextArea } from '../../../components/TextArea'
import { useAppSelector } from '../../../hooks/useAppSelector'
import { useDialogBox } from '../../../hooks/useDialogBox'
import { usePermission } from '../../../hooks/usePermission'
import { usePromotersStayingTimeContext } from '../../../hooks/usePromotersStayingTimeContext'
import { ProductProblem, productProblemsMap } from '../../../interfaces/productProblemsEnum'
import { Role } from '../../../interfaces/rolesEnum'
import { cn } from '../../../libs/tailwindMerge'
import { SelectOption } from '../../../reducers/appReducer'
import { getAllProducts } from '../../../services/productsServices'
import {
  addProductProblem,
  checkProductProblems,
  CleanReport,
  generateTemporaryAccessLink,
  getReport,
  Report,
  toggleHasPromoterProblem,
  updateReportObservations,
  updateReportPromoterNotes,
} from '../../../services/reportServices'
import { exportToExcel } from '../../../utils/exportToExcel'
import { formatDateRelativeTime } from '../../../utils/formatDateRelativeTime'

const observationsFormSchema = z.object({
  manager_observations: z.string().optional(),
  improvement_suggestions: z.string().optional(),
  general_observations: z.string().optional(),
})

type ObservationsFormData = z.infer<typeof observationsFormSchema>

const promoterNotesFormSchema = z.object({
  promoter_notes: z.string().optional(),
})

type PromoterNotesFormData = z.infer<typeof promoterNotesFormSchema>

const newProductProblemFormSchema = z.object({
  id: z.string().optional(),
  problem: z.nativeEnum(ProductProblem),
  product: z.object({
    id: z.string(),
    name: z.string(),
    code: z.string().optional(),
  }),
  qty: z
    .union([z.string(), z.number().nullable(), z.undefined()])
    .transform((value) =>
      value === null || value === undefined || value === ''
        ? null
        : typeof value === 'string'
          ? parseFloat(value)
          : value,
    ),
  expire_date: z
    .string()
    .nullable()
    .optional()
    .transform((value) => {
      if (!value) return null
      const date = new Date(value)
      date.setMinutes(date.getMinutes() + date.getTimezoneOffset())
      return date.toISOString().split('T')[0]
    }),
})

type NewProductProblemFormData = z.infer<typeof newProductProblemFormSchema>

export function ReportDetails() {
  const [loadedReport, setLoadedReport] = useState<Report | undefined>()
  const [selectedImage, setSelectedImage] = useState<Report['report_images'][number] | null>(null)

  const [productsSelect, setProductsSelect] = useState<SelectOption[]>([])

  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [isAddImagesModalOpen, setIsAddImagesModalOpen] = useState(false)

  const [isEdittingObservations, setIsEdittingObservations] = useState(false)
  const [isEdittingPromoterNotes, setIsEdittingPromoterNotes] = useState(false)
  const [isAddingProductProblem, setIsAddingProductProblem] = useState(false)

  const { startNewCycle, activeCycle, isLoadingGeolocation } = usePromotersStayingTimeContext()

  const { showDialog } = useDialogBox()

  const { id } = useParams()
  const {
    upsertReport,
    editReportSellerData,
    viewImages,
    seeAllPromoters,
    seeReportStoreSeller,
    canAddProductProblem,
    isObserver,
    updatepromoterNotes,
    canExportReports,
    canGenerateTemporaryLink,
  } = usePermission()
  const { profile } = useAppSelector(state => state.auth)

  const { data: report, refetch } = useQuery({
    queryKey: ['report_detail', id],
    queryFn: async () => {
      if (id) {
        return await getReport(id)
      }
      return {
        id: '',
        user: {} as Report['user'],
        general_observations: '',
        improvement_suggestions: '',
        manager_observations: '',
        promoter_notes: '',
        report_images: [],
        report_product_problem: [],
        updated_at: new Date(),
        has_promoter_problem: false,
        store: {} as Report['store'],
        trade_marketing_check_date: new Date(),
      }
    },
  })

  const { register: registerPromoterNotesForm, handleSubmit: handleSubmitPromoterNotesForm } = useForm<PromoterNotesFormData>({
    resolver: zodResolver(promoterNotesFormSchema),
    defaultValues: {
      promoter_notes: report?.promoter_notes,
    },
  })

  const { register: registerObservationsForm, handleSubmit: handleSubmitObservationsForm, setFocus } = useForm<ObservationsFormData>({
    resolver: zodResolver(observationsFormSchema),
    defaultValues: {
      general_observations: report?.general_observations,
      improvement_suggestions: report?.improvement_suggestions,
      manager_observations: report?.manager_observations,
    },
  })

  const {
    register: registerNewProductProblemForm,
    handleSubmit: handleSubmitNewProductProblemForm,
    control,
    watch,
    reset,
    setValue,
    formState: {
      isSubmitting,
    },
  } = useForm<NewProductProblemFormData>({
    resolver: zodResolver(newProductProblemFormSchema),
  })

  function handleZoomImage(image: Report['report_images'][number]) {
    setSelectedImage(image)
    setIsImageModalOpen(true)
  }

  function handleCloseImageModal() {
    setSelectedImage(null)
    setIsImageModalOpen(false)
  }

  function handleStartEdittingObservations() {
    setIsEdittingObservations(true)
    setFocus('manager_observations')
  }

  function handleStartEdittingObservationPromoterNotes() {
    setIsEdittingPromoterNotes(true)
  }

  function handleAddNewProductProblem() {
    if (profile?.role === 'PROMOTER' && !activeCycle) {
      showDialog({
        title: 'Você ainda não deu entrada nessa loja!',
        description: 'Para relatar um problema com produtos, é preciso primeiro dar entrada na loja 👍',
      })
    } else {
      setIsAddingProductProblem(true)
    }
  }

  function handleCancelAddNewProductProblem() {
    setIsAddingProductProblem(false)
    reset()
  }

  async function handleToggleHasPromoterProblem() {
    if (report) {
      const result = await toggleHasPromoterProblem(report.id)
      if (result) {
        if (result.success) {
          setLoadedReport((prev) => {
            return {
              ...prev as Report,
              has_promoter_problem: result.report!.has_promoter_problem,
            }
          })
          toast.success(result.message, { position: 'top-left' })
        } else {
          toast.error(result.message, { position: 'top-left' })
        }
      }
    }
  }

  async function handleCheckProductProblem(productProblemId: string) {
    if (report) {
      const confirmation = confirm('Tem certeza que deseja marcar esse problema com produto como "resolvido"?')
      if (!confirmation) return
      const result = await checkProductProblems(productProblemId, report.id)
      const filteredList = loadedReport?.report_product_problem.filter(item => item.id !== productProblemId)
      if (result) {
        setLoadedReport((prev) => {
          return {
            ...prev as Report,
            report_product_problem: filteredList as Report['report_product_problem'],
          }
        })
      }
    }
  }

  async function handleSubmitObservations(data: ObservationsFormData) {
    if (report) {
      await updateReportObservations({
        id: report.id,
        general_observations: data.general_observations,
        manager_observations: data.manager_observations,
        improvement_suggestions: data.improvement_suggestions,
      })
      setIsEdittingObservations(false)
      refetch()
    }
  }

  async function handleSubmitPromoterNotes(data: PromoterNotesFormData) {
    if (report) {
      await updateReportPromoterNotes({
        id: report.id,
        promoter_notes: data.promoter_notes ?? '',
      })
      setIsEdittingPromoterNotes(false)
      refetch()
    }
  }

  async function handleSubmitAddNewProductProblem(data: NewProductProblemFormData) {
    if (report) {
      await addProductProblem({
        ...data,
        report_id: report.id,
      })
      setIsAddingProductProblem(false)
      refetch()
    }
  }

  function toggleAddImagesModal() {
    if (!loadedReport) return

    if (profile?.role === Role.PROMOTER) {
      if (activeCycle) {
        if (activeCycle.reportId === loadedReport.id) {
          if (isAddImagesModalOpen) {
            refetch()
          }
          setIsAddImagesModalOpen(open => !open)
        } else {
          toast.error(
            `Há um registro aberto em outra loja. Você precisa registrar
            sua chegada nesta loja para adicionar imagens!`,
            { position: 'top-left' },
          )
        }
      } else {
        toast.error('Você precisa registrar sua chegada na loja para adicionar imagens!', { position: 'top-left' })
      }
    } else {
      setIsAddImagesModalOpen(open => !open)
    }
  }

  const selectedProduct = watch('product')
  const selectedProblem = watch('problem')

  async function handleExportToExcel() {
    await exportToExcel([report as CleanReport], `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`)
  }

  async function handleGenerateTemporaryAccessLink() {
    if (!loadedReport) return
    const result = await generateTemporaryAccessLink(loadedReport.id)
    if (result) {
      if (result.success) {
        const shareUrl = encodeURIComponent(result.data!)
        const text = encodeURIComponent(`Acesso para o relatório para ${loadedReport.store.name} em ${loadedReport.store.city} - ${loadedReport.store.state}`)

        navigator.clipboard.writeText(result.data!)
        showDialog({
          title: 'Link gerado com sucesso!',
          action: () => {
            window.open(`https://api.whatsapp.com/send?text=${text} ${shareUrl}`, '_blank')
          },
          type: 'success',
          showConfirmation: true,
          cancelButtonText: 'Copiar link',
          confirmButtonText: 'Compartilhar no WhatsApp',
          cancelAction: () => {
            toast.success('Link copiado para a área de transferência!', { position: 'top-left' })
          },
        })
      } else {
        showDialog({
          title: 'Erro ao gerar link temporário',
          description: result.message,
          type: 'error',
        })
      }
    }
  }

  async function handleClickShareTemporaryLink() {
    return new Promise<void>(resolve => {
      showDialog({
        title: 'Tem certeza que deseja gerar um link de acesso temporário?',
        description: 'Esse link poderá ser acessado por qualquer pessoa que o tiver pelas próximas 48hs.',
        type: 'warning',
        showConfirmation: true,
        action: () => {
          handleGenerateTemporaryAccessLink()
          resolve()
        },
      })
    })
  }

  useEffect(() => {
    setLoadedReport(report)
  }, [report])

  const defineSelects = useCallback(async () => {
    const [{ products }] = await Promise.all([
      getAllProducts(),
    ])
    setProductsSelect(products.map(item => ({ label: item.name, value: item.id })))
  }, [])

  useEffect(() => {
    defineSelects()
  }, [defineSelects])

  if (!report || !loadedReport) {
    return (
      <div className="flex flex-col mx-auto max-w-screen-lg">
        Loading...
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <FloatingPromotersStayingTimeCounter />
      <div className="flex flex-col mx-auto max-w-screen-lg pb-16">
        <div className="px-2 lg:px-0 my-4">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/dashboard' },
              { label: 'Detalhes do relatório', path: '#' },
            ]}
          />
        </div>
        <header className="px-2 mt-4">
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <strong className="text-lg text-gray-700">
                ID. Relatório
              </strong>
              <span className="text-xs text-gray-400">{loadedReport.id}</span>
            </div>
            {(canExportReports || canGenerateTemporaryLink) && (
              <div className="flex ml-auto gap-2">
                {canGenerateTemporaryLink && (
                  <Button
                    className="w-fit"
                    title="Exportar com link de acesso temporário"
                    type="button"
                    onClick={() => handleClickShareTemporaryLink()}
                  >
                    <RiShareBoxLine />
                  </Button>
                )}
                {canExportReports && (
                  <Button
                    className="w-fit"
                    type="button"
                    onClick={() => handleExportToExcel()}
                    disabled={!report}
                  >
                    <PiMicrosoftExcelLogoFill />
                  </Button>
                )}
              </div>
            )}
            <div className="flex flex-col items-end ml-2">
              {(profile?.role === Role.PROMOTER || profile?.role === Role.ROOT) && (
                <Button
                  title="Informar chegada na loja"
                  className="w-fit p-3 disabled:bg-slate-500"
                  isLoading={isLoadingGeolocation}
                  disabled={!!activeCycle}
                  onClick={() => startNewCycle(loadedReport.id)}
                >
                  <MdTimer className="sm:mr-2" />
                  <span className="hidden sm:inline">
                    Informar chegada
                  </span>
                </Button>
              )}
            </div>
          </div>
          <div className="flex mt-8 items-center">
            <img
              src={loadedReport.store.group.logo_url}
              alt={loadedReport.store.group.name}
              className="w-12 h-12 rounded-full object-contain border-2 mr-2"
            />
            <div className="flex flex-col">
              <strong className="text-2xl">{loadedReport.store.name}</strong>
              <span className="font-semibold text-gray-600">
                {loadedReport.store.city} - {loadedReport.store.state}
              </span>
            </div>
          </div>
        </header>
        <main className="px-2 mt-8">
          <strong className="text-xl border-b pb-1 pr-4 border-gray-400">Problemas identificados</strong>
          <section className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <strong className="text-lg pl-2 text-gray-800 font-semibold">Produtos</strong>
              <div className="flex space-x-2">
                {seeReportStoreSeller && (
                  <Dropdown
                    toggleIcon={RiUser2Fill}
                    icon={FaX}
                    triggerClassName="flex w-full justify-center items-center text-sm/6 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition bg-indigo-600 text-white hover:bg-indigo-500 shadow-sm focus-visible:outline-indigo-600 border-0 text-white hover:text-white"
                  >
                    {!loadedReport.store.seller
                      ? (
                        <div
                          className={cn(
                            'flex justify-between py-3 px-2',
                          )}
                        >
                          <span className="text-nowrap">Sem vendedor</span>
                        </div>
                        )
                      : (
                        <div
                          className={cn(
                            'flex justify-between py-3 px-2',
                          )}
                        >
                          <span className="whitespace-nowrap">{loadedReport.store.seller.name}</span>
                          <a
                            target="_blank"
                            href={`https://wa.me/55${loadedReport.store.seller.phone}`}
                            rel="noreferrer"
                            className="flex items-center justify-center bg-green-400 hover:bg-green-600 cursor-pointer text-white p-1 rounded-md ml-4"
                            title="Abrir contato no WhatsApp"
                          >
                            <FaWhatsapp />
                          </a>
                        </div>
                        )}
                  </Dropdown>
                )}
                {canAddProductProblem && !isObserver &&
                  <>
                    {!isAddingProductProblem &&
                      <Button
                        type="button"
                        title="Adicionar"
                        onClick={handleAddNewProductProblem}
                      >
                        <FaPencilAlt />
                      </Button>}
                    {isAddingProductProblem &&
                      <Button
                        type="submit"
                        title="Encerrar"
                        onClick={handleCancelAddNewProductProblem}
                      >
                        <MdCancel />
                      </Button>}
                  </>}
              </div>
            </div>
            {
              isAddingProductProblem && (
                <form className="block p-2 mb-8 bg-white rounded-md border" onSubmit={handleSubmitNewProductProblemForm(handleSubmitAddNewProductProblem)}>
                  <div>
                    <Controller
                      name="product"
                      control={control}
                      render={({ field }) => (
                        <>
                          <label
                            className="block text-lg font-medium text-gray-900 mb-1"
                            htmlFor="product"
                          >
                            Produto
                          </label>
                          <Select
                            id="product"
                            options={productsSelect}
                            onChange={state => {
                              field.onChange({ id: state?.value, name: state?.label })
                              setValue('problem', ProductProblem.RUPTURE)
                            }}
                            placeholder="Selecione..."
                          />
                        </>
                      )}
                    />
                  </div>
                  {selectedProduct && (
                    <div className="flex flex-col justify-between mb-2 rounded-md mt-4">
                      <div className="flex items-center">
                        <span className="flex-1 text-sm font-semibold">{selectedProduct.name}</span>
                      </div>
                      <div className="flex">
                        <div className="flex w-full items-center border border-gray-300 rounded-md overflow-hidden bg-white p-1 justify-center">
                          <Controller
                            name="problem"
                            control={control}
                            render={({ field }) => (
                              <select
                                {...field}
                                id="product_problems_select"
                                value={field.value || ProductProblem.RUPTURE}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) => field.onChange(e.target.value)}
                                className="h-6 flex-1 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                              >
                                {Object.values(ProductProblem).map(prob => (
                                  <option key={prob} value={prob}>{productProblemsMap[prob]}</option>
                                ))}
                              </select>
                            )}
                          />
                          {selectedProblem === ProductProblem.EXPIRED &&
                            (
                              <input
                                type="date"
                                className="w-32 text-right border-l ml-1 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                {...registerNewProductProblemForm('expire_date')}
                              />
                            )}
                          <Controller
                            name="qty"
                            control={control}
                            render={({ field }) => (
                              <input
                                {...field}
                                type="number"
                                className="w-10 text-right border-l ml-1 rounded-md focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                                placeholder="0"
                                min={0}
                                value={field.value || ''}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
                              />
                            )}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end mt-4">
                        <Button isLoading={isSubmitting} className="w-fit" type="submit">
                          Adicionar
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
              )
            }
            {loadedReport.report_product_problem.length > 0
              ? (
                <div className="overflow-x-auto mt-4 md:mt-2 w-full border border-slate-500 rounded-lg">
                  <table className="flex-1 w-full text-sm text-left rounded-t-md overflow-hidden">
                    <thead className="text-xs uppercase bg-slate-50 bg-opacity-80">
                      <tr>
                        <th className="p-2 border-r border-slate-500">Produto - Cód.</th>
                        <th className="p-2 border-r border-slate-500 text-center">Data da criação</th>
                        <th className="p-2 border-r border-slate-500 text-center">Tipo</th>
                        <th className="p-2 border-r border-slate-500 text-center">Data</th>
                        <th className="p-2 border-r border-slate-500 text-right">Qtd.</th>
                        <th className="w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {loadedReport.report_product_problem.map(item => (
                        <tr key={item.id} className="border-t border-slate-500 hover:bg-white_transparency hover:bg-opacity-15">
                          <td
                            scope="row"
                            className="p-1 font-medium whitespace-nowrap border-r border-slate-500"
                          >
                            {`${item.product.name}${item.product.code && ` - ${item.product.code}`}`}
                          </td>
                          <td
                            scope="row"
                            className="p-1 font-medium whitespace-nowrap border-r border-slate-500 text-center"
                          >
                            {
                              new Intl.DateTimeFormat('pt-BR', {
                                timeZone: 'UTC',
                              })
                                .format(
                                  new Date(item.created_at),
                                )
                            }
                          </td>
                          <td
                            scope="row"
                            className="p-1 font-medium whitespace-nowrap border-r border-slate-500 text-center"
                          >
                            {productProblemsMap[item.problem]}
                          </td>
                          <td
                            scope="row"
                            className="p-1 font-medium whitespace-nowrap border-r border-slate-500 text-center"
                          >
                            {item.expire_date
                              ? (
                                <>
                                  {
                                    new Intl.DateTimeFormat('pt-BR', {
                                      timeZone: 'UTC',
                                    })
                                      .format(
                                        new Date(item.expire_date),
                                      )
                                  }
                                </>
                                )
                              : (
                                <span>---</span>
                                )}
                          </td>
                          <td
                            scope="row"
                            className="p-1 pr-2 font-medium whitespace-nowrap border-r border-slate-500 text-right"
                          >
                            {item.qty || '---'}
                          </td>
                          <td
                            scope="row"
                            className="p-1 font-medium whitespace-nowrap text-right w-10"
                          >
                            <Button
                              type="button"
                              className="disabled:bg-gray-300 disabled:cursor-not-allowed"
                              title="Marcar como solucionado"
                              size="icon"
                              onClick={() => handleCheckProductProblem(item.id)}
                              disabled={!editReportSellerData || isObserver}
                            >
                              <FaCheck />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )
              : (
                <div className="flex items-center justify-center border-2 rounded-lg border-gray-300 border-dashed p-4">
                  <span>Nenhum</span>
                </div>
                )}
          </section>
          <section className="mt-6">
            <div className="flex items-center justify-between">
              <strong className="text-lg pl-2 text-gray-800 font-semibold">Promoção dos itens</strong>
              <div className="flex space-x-2">
                {seeAllPromoters && (
                  <Dropdown
                    toggleIcon={FaBuildingUser}
                    icon={FaX}
                    triggerClassName="flex w-full justify-center items-center text-sm/6 font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition bg-indigo-600 text-white hover:bg-indigo-500 shadow-sm focus-visible:outline-indigo-600 border-0 text-white hover:text-white"
                  >
                    {report.store.store_promoter.map(({ promoter }) => (
                      <div
                        key={promoter.id}
                        className={cn(
                          'flex justify-between py-3 px-2',
                          report.store.store_promoter.length > 1 && 'border-b border-gray-400',
                        )}
                      >
                        <span className="whitespace-nowrap">{promoter.name}</span>
                        <a
                          target="_blank"
                          href={`https://wa.me/55${promoter.phone}`}
                          rel="noreferrer"
                          className="flex items-center justify-center bg-green-400 hover:bg-green-600 cursor-pointer text-white p-1 rounded-md ml-4"
                          title="Abrir contato no WhatsApp"
                        >
                          <FaWhatsapp />
                        </a>
                      </div>
                    ))}
                  </Dropdown>
                )}
                {!isObserver && (
                  <>
                    <Button
                      type="button"
                      title="Adicionar imagens"
                      onClick={toggleAddImagesModal}
                      hasPermission={viewImages}
                    >
                      <MdAddPhotoAlternate />
                    </Button>
                    {upsertReport &&
                      <>
                        {loadedReport.has_promoter_problem
                          ? (
                            <Button
                              type="button"
                              className="bg-green-400 hover:bg-green-600"
                              title="Marcar como solucionado"
                              onClick={handleToggleHasPromoterProblem}
                            >
                              <ImCheckboxChecked />
                            </Button>
                            )
                          : (
                            <Button
                              type="button"
                              className="bg-red-400 hover:bg-red-600"
                              title="Marcar como pendente"
                              onClick={handleToggleHasPromoterProblem}
                            >
                              <ImCheckboxUnchecked />
                            </Button>
                            )}
                      </>}
                  </>
                )}
              </div>
            </div>
            {loadedReport.report_images.length === 0
              ? (
                <div className="flex w-full p-4 items-center justify-center border-2 rounded-md mt-4">
                  <span className="flex flex-col items-center font-semibold text-gray-500">
                    <MdOutlineImageNotSupported size={55} />
                    Nenhuma imagem
                  </span>
                </div>
                )
              : (
                <div className="max-h-[37.5rem] overflow-y-auto mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {loadedReport.report_images.map(image => (
                      <div
                        key={image.id}
                        className="overflow-hidden rounded-md relative"
                      >
                        <img
                          className="object-contain cursor-pointer"
                          src={image.url}
                          loading="lazy"
                          onClick={() => handleZoomImage(image)}
                        />
                        <span className={cn(
                          'flex items-center justify-center text-white absolute h-8 w-6 border border-t-0 rounded-b-full top-0 right-2',
                          differenceInCalendarDays(new Date(), image.created_at) < 1 && 'border-green-400 bg-green-600/80',
                          differenceInCalendarDays(new Date(), image.created_at) === 1 && 'border-amber-400 bg-amber-600/80',
                          differenceInCalendarDays(new Date(), image.created_at) > 1 && 'border-red-400 bg-red-600/80',
                        )}
                        >
                          {differenceInCalendarDays(new Date(), image.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                )}
            {loadedReport.promoters_staying_time && (
              <>
                <strong className="block mt-6">Registro de permanência em loja</strong>
                <main className="lg:px-0 flex-1 h-full">
                  <div
                    className="overflow-auto mt-4 md:mt-2 w-full max-h-[13rem] border border-slate-500 rounded-lg"
                  >
                    <table
                      className="flex-1 w-full text-sm text-left rounded-t-md overflow-hidden"
                    >
                      <thead className="text-xs uppercase bg-primary_700 bg-opacity-80">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3 min-w-48 text-center border-r border-slate-500"
                          >
                            Promotor
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 border-r border-slate-500 text-center"
                          >
                            Dia
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 border-r border-slate-500 text-center"
                          >
                            Tempo
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 w-16 text-center"
                          >
                            Locais
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadedReport.promoters_staying_time.map(item => (
                          <tr
                            key={item.id}
                            className="border-t border-slate-500 hover:bg-white_transparency hover:bg-opacity-15"
                          >
                            <td
                              scope="row"
                              className="font-medium whitespace-nowrap sticky left-0 border-r border-slate-500 pl-2"
                            >
                              <div className="flex items-center">
                                <div className="rounded-md overflow-hidden p-2">
                                  <Avatar className="w-10 h-10 rounded-md border border-gray-300" imageUrl={item.promoter.avatar_url} />
                                </div>
                                <span>{item.promoter.name}</span>
                              </div>
                            </td>
                            <td
                              scope="row"
                              className="font-medium text-dark whitespace-nowrap text-center sticky left-0 border-r border-slate-500 p-2"
                            >
                              <span>
                                {
                                  new Intl.DateTimeFormat('pt-BR', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric',
                                  }).format(new Date(item.entry_time))
                                }
                              </span>
                            </td>
                            <td
                              scope="row"
                              className="font-medium text-dark whitespace-nowrap text-center sticky left-0 border-r border-slate-500 p-2"
                            >
                              {item.expired
                                ? (
                                  <span className="font-semibold">EXPIRADO</span>
                                  )
                                : (
                                  <>
                                    {item.leave_time
                                      ? (
                                        <span>
                                          {
                                        formatDistance(item.entry_time, item.leave_time, { locale: ptBR })
                                      }
                                        </span>
                                        )
                                      : (
                                        <span>Em andamento</span>
                                        )}
                                  </>
                                  )}
                            </td>
                            <td
                              scope="row"
                              className="font-medium text-dark whitespace-nowrap sticky p-2 text-center"
                            >
                              <div className="flex flex-row justify-center gap-2">
                                <a
                                  className="rounded-lg flex items-center justify-center p-3 bg-amber-600 hover:bg-amber-800 text-white transition-colors"
                                  href={`https://www.google.com/maps?q=${item.entry_time_coordinates.split(':')[0]},${item.entry_time_coordinates.split(':')[1]}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <SiGooglemaps />
                                  <BsBoxArrowInDown />
                                </a>
                                {item.leave_time_coordinates && (
                                  <a
                                    className="rounded-lg flex items-center justify-center p-3 bg-amber-600 hover:bg-amber-800 text-white transition-colors"
                                    href={`https://www.google.com/maps?q=${item.leave_time_coordinates.split(':')[0]},${item.leave_time_coordinates.split(':')[1]}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <SiGooglemaps />
                                    <BsBoxArrowUp />
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </main>
              </>
            )}
          </section>
          <form onSubmit={handleSubmitPromoterNotesForm(handleSubmitPromoterNotes)} className="mt-4">
            <div className="flex items-center justify-between">
              <strong className="text-xl border-b pb-1 pr-4 border-gray-400">Anotações do promotor</strong>
              {!isObserver && (
                <>
                  {updatepromoterNotes &&
                    <>
                      {!isEdittingPromoterNotes &&
                        <Button
                          type="button"
                          title="Editar"
                          size="icon"
                          onClick={handleStartEdittingObservationPromoterNotes}
                        >
                          <FaPencilAlt />
                        </Button>}
                      {isEdittingPromoterNotes &&
                        <Button
                          type="submit"
                          title="Salvar"
                          size="icon"
                        >
                          <FaSave />
                        </Button>}
                    </>}
                </>
              )}
            </div>
            <section className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <TextArea
                  label="Observações, anotações, etc..."
                  defaultValue={loadedReport.promoter_notes}
                  readOnly={!isEdittingPromoterNotes}
                  {...registerPromoterNotesForm('promoter_notes')}
                />
              </div>
            </section>
          </form>
          <form onSubmit={handleSubmitObservationsForm(handleSubmitObservations)} className="mt-4">
            <div className="flex items-center justify-between">
              <strong className="text-xl border-b pb-1 pr-4 border-gray-400">Observações</strong>
              {!isObserver && (
                <>
                  {upsertReport &&
                    <>
                      {!isEdittingObservations &&
                        <Button
                          type="button"
                          title="Editar"
                          size="icon"
                          onClick={handleStartEdittingObservations}
                        >
                          <FaPencilAlt />
                        </Button>}
                      {isEdittingObservations &&
                        <Button
                          type="submit"
                          title="Salvar"
                          size="icon"
                        >
                          <FaSave />
                        </Button>}
                    </>}
                </>
              )}
            </div>
            <section className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <TextArea
                  label="Observações do gerente"
                  defaultValue={loadedReport.manager_observations}
                  readOnly={!isEdittingObservations}
                  {...registerObservationsForm('manager_observations')}
                />
              </div>
              <div>
                <TextArea
                  label="Sugestões de melhorias"
                  defaultValue={loadedReport.improvement_suggestions}
                  readOnly={!isEdittingObservations}
                  {...registerObservationsForm('improvement_suggestions')}
                />
              </div>
              <div>
                <TextArea
                  label="Observações gerais"
                  defaultValue={loadedReport.general_observations}
                  readOnly={!isEdittingObservations}
                  {...registerObservationsForm('general_observations')}
                />
              </div>
            </section>
          </form>
        </main>
      </div>
      {isImageModalOpen && selectedImage && (
        <div
          className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
          onClick={() => handleCloseImageModal()}
        >
          <div
            className="flex flex-col bg-slate-100 max-w-[90vw] max-h-[90dvh] relative rounded-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={selectedImage.url} />
            <div className="absolute right-2 top-2">
              <a
                className="rounded-lg flex items-center justify-center p-3 bg-white/50 hover:bg-yellow-500/50 hover:text-white"
                href={`https://www.google.com/maps?q=${selectedImage.latitude},${selectedImage.longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                <SiGooglemaps />
              </a>
            </div>
            <footer className="absolute w-full bottom-0 left-0 bg-black/60 p-4">
              <span className="text-white font-semibold">Foto cadastrada {formatDateRelativeTime(selectedImage.created_at)}</span>
            </footer>
          </div>
        </div>
      )}
      {isAddImagesModalOpen &&
        <ModalAddImages
          report={loadedReport}
          onClose={toggleAddImagesModal}
        />}
    </div>
  )
}
